import { Inject, Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, BehaviorSubject, switchMap, filter, take } from 'rxjs';

import { AuthenticationResponseModel } from '../../../account/core/models/auth.model';

import { AuthService } from '../../../account/core/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorInterceptor implements HttpInterceptor {

  private refreshing: boolean = false;

  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(@Inject(Injector) private injector: Injector
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Handle 401 Unauthorized
          if (error.status === 401) {
            return this.handleUnauthenticatedError(request, next);
          }
          else {
            return throwError(error);
          }
        })
      );
  }

  private handleUnauthenticatedError(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.refreshing) {
      this.refreshing = true;
      this.refreshTokenSubject.next(null);

      // Get current token
      let authenticationService = this.injector.get(AuthService);
      let token = authenticationService.token;
      if (token) {

        // Refresh current token 
        return authenticationService.refreshToken(token)
          .pipe(
            switchMap((result: AuthenticationResponseModel) => {
              if (!result) {
                throw new Error("Failed to reauthenticate");
              }

              this.refreshing = false;
              this.refreshTokenSubject.next(result.accessToken);

              return next.handle(this.updateAuthenticationHeader(request, result.accessToken));
            }),
            catchError((error: any) => {
              this.refreshing = false;
              authenticationService.logout();

              return throwError(() => error);
            })
          );
      }
    }

    return this.refreshTokenSubject.pipe(
      filter((token: string) => token !== null),
      take(1),
      switchMap((token: string) => next.handle(this.updateAuthenticationHeader(request, token)))
    );
  }

  private updateAuthenticationHeader(request: HttpRequest<any>, token: string | null) {
    return request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) });
  }
}
